/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState } from "react";
import { css } from "@emotion/react";
import {
  deleteStorageFile,
  listStorageFiles,
  uploadStorageFile,
} from "../../../services/storage.service";

type StorageAsset = {
  key: string;
  url: string;
  size: number;
  lastModified: string;
  folder: string;
  name: string;
};

type MediaManagerProps = {
  initialFolder?: string;
  onSelect?: (asset: StorageAsset) => void;
};

const folders = [
  "banners",
  "logos",
  "portfolio",
  "founders",
  "staff",
  "videos",
  "documents",
];

const root = css`
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 24px;
`;

const card = css`
  background: #ffffff;
  border: 1px solid #eef0f5;
  border-radius: 22px;
  padding: 22px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.04);
`;

const title = css`
  margin: 0 0 8px;
  color: #001740;
  font-size: 24px;
  font-weight: 800;
`;

const subtitle = css`
  margin: 0 0 20px;
  color: #64748b;
  font-size: 14px;
`;

const folderButton = (active: boolean) => css`
  width: 100%;
  border: 0;
  border-radius: 14px;
  padding: 12px 14px;
  margin-bottom: 8px;
  text-align: left;
  cursor: pointer;
  font-weight: 700;
  color: ${active ? "#001740" : "#475569"};
  background: ${active ? "#eef5ff" : "transparent"};

  &:hover {
    background: #f8fbff;
  }
`;

const toolbar = css`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 12px;
  margin-bottom: 18px;
  align-items: center;
`;

const input = css`
  border: 1px solid #d8dee9;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 14px;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #34189a;
    box-shadow: 0 0 0 4px rgba(52, 24, 154, 0.08);
  }
`;

const uploadZone = (dragging: boolean) => css`
  border: 1.5px dashed ${dragging ? "#34189a" : "#b7a7ff"};
  background: ${dragging ? "#f6f2ff" : "#faf8ff"};
  border-radius: 18px;
  padding: 24px;
  margin-bottom: 24px;
  cursor: pointer;
  transition: all 0.18s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;

  &:hover {
    border-color: #34189a;
    background: #f6f2ff;
  }
`;

const button = css`
  border: 0;
  border-radius: 12px;
  background: #34189a;
  color: white;
  padding: 12px 18px;
  font-weight: 800;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ghostButton = css`
  border: 1px solid #d8dee9;
  border-radius: 12px;
  background: #ffffff;
  color: #001740;
  padding: 12px 18px;
  font-weight: 800;
  cursor: pointer;
`;

const grid = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 18px;
`;

const assetCard = css`
  border: 1px solid #eef0f5;
  border-radius: 18px;
  overflow: hidden;
  background: #ffffff;
`;

const preview = css`
  height: 220px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const assetBody = css`
  padding: 14px;
`;

const assetName = css`
  color: #001740;
  font-weight: 800;
  font-size: 13px;
  word-break: break-word;
  margin-bottom: 8px;
`;

const assetMeta = css`
  color: #64748b;
  font-size: 12px;
  margin-bottom: 12px;
`;

const actions = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  button {
    border: 0;
    border-radius: 10px;
    padding: 8px 10px;
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;
  }
`;

const copyButton = css`
  background: #eef5ff;
  color: #001740;
`;

const selectButton = css`
  background: #ecfdf5;
  color: #047857;
`;

const deleteButton = css`
  background: #fef2f2;
  color: #991b1b;
`;

const infoBox = css`
  background: #f8fafc;
  border: 1px solid #eef0f5;
  border-radius: 16px;
  padding: 14px;
  color: #475569;
  font-size: 13px;
  margin-bottom: 18px;
`;

const MediaManager: React.FC<MediaManagerProps> = ({
  initialFolder = "banners",
  onSelect,
}) => {
  const [folder, setFolder] = useState(initialFolder);
  const [customFolder, setCustomFolder] = useState("");
  const [fileName, setFileName] = useState("");
  const [search, setSearch] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [assets, setAssets] = useState<StorageAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const currentFolder = customFolder || folder;

  const filteredAssets = useMemo(() => {
    return assets.filter((asset) =>
      `${asset.name} ${asset.key}`.toLowerCase().includes(search.toLowerCase())
    );
  }, [assets, search]);

  const loadFiles = async () => {
    setLoading(true);

    try {
      const data = await listStorageFiles(currentFolder);
      setAssets(data || []);
    } catch (error) {
      console.error("[MEDIA_LIST_ERROR]", error);
      alert("No se pudo cargar el storage.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder, customFolder]);

  const handleUpload = async () => {
    if (!file) return;

    setLoading(true);

    try {
      await uploadStorageFile({
        file,
        folder: currentFolder,
        name: fileName,
      });

      setFile(null);
      setFileName("");
      await loadFiles();
    } catch (error) {
      console.error("[MEDIA_UPLOAD_ERROR]", error);
      alert("No se pudo subir el archivo.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (url: string) => {
    await navigator.clipboard.writeText(url);
    alert("URL copiada");
  };

  const handleDelete = async (key: string) => {
    const ok = window.confirm("¿Eliminar este archivo?");
    if (!ok) return;

    setLoading(true);

    try {
      await deleteStorageFile(key);
      await loadFiles();
    } catch (error) {
      console.error("[MEDIA_DELETE_ERROR]", error);
      alert("No se pudo eliminar el archivo.");
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);

    const droppedFile = event.dataTransfer.files?.[0];

    if (droppedFile) {
      setFile(droppedFile);
    }
  };

  const isVideo = (url: string) => /\.(mp4|webm|mov)$/i.test(url);
  const isImage = (url: string) => /\.(png|jpg|jpeg|svg|webp|gif)$/i.test(url);

  return (
    <div css={root}>
      <aside css={card}>
        <h2 css={title}>Storage</h2>
        <p css={subtitle}>Organiza imágenes, videos y archivos del sitio.</p>

        {folders.map((item) => (
          <button
            key={item}
            css={folderButton(folder === item && !customFolder)}
            onClick={() => {
              setFolder(item);
              setCustomFolder("");
            }}
          >
            📁 {item}
          </button>
        ))}
      </aside>

      <section css={card}>
        <h2 css={title}>Biblioteca multimedia</h2>
        <p css={subtitle}>
          Carpeta actual: <strong>{currentFolder}</strong>
        </p>

        <div css={toolbar}>
          <input
            css={input}
            value={customFolder}
            onChange={(e) => setCustomFolder(e.target.value)}
            placeholder="Subcarpeta: banners/home"
          />

          <input
            css={input}
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            placeholder="Nombre personalizado: banner-home-01"
          />

          <button css={ghostButton} onClick={loadFiles} disabled={loading}>
            Actualizar
          </button>
        </div>

        <div
          css={uploadZone(dragging)}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => document.getElementById("media-file-input")?.click()}
        >
          <div>
            <strong>
              {file ? file.name : "Arrastra un archivo o haz clic para subir"}
            </strong>
            <div style={{ color: "#64748b", marginTop: 6 }}>
              PNG, JPG, SVG, WEBP, GIF, MP4, WEBM, PDF.
            </div>
          </div>

          <button css={button} disabled={!file || loading} onClick={handleUpload}>
            {loading ? "Procesando..." : "+ Subir"}
          </button>

          <input
            id="media-file-input"
            type="file"
            style={{ display: "none" }}
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </div>

        <div css={infoBox}>
          Al subir, se generará una URL pública para copiar y usar en cualquier
          sección del CMS.
        </div>

        <input
          css={input}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar archivo..."
        />

        <div style={{ height: 24 }} />

        {loading && <p>Cargando archivos...</p>}

        <div css={grid}>
          {filteredAssets.map((asset) => (
            <article key={asset.key} css={assetCard}>
              <div css={preview}>
                {isImage(asset.url) && <img src={asset.url} alt={asset.name} />}
                {isVideo(asset.url) && <video src={asset.url} controls />}
                {!isImage(asset.url) && !isVideo(asset.url) && <span>📄</span>}
              </div>

              <div css={assetBody}>
                <div css={assetName}>{asset.name}</div>
                <div css={assetMeta}>
                  {(asset.size / 1024).toFixed(1)} KB
                </div>

                <div css={actions}>
                  {onSelect && (
                    <button css={selectButton} onClick={() => onSelect(asset)}>
                      Seleccionar
                    </button>
                  )}

                  <button css={copyButton} onClick={() => handleCopy(asset.url)}>
                    Copiar URL
                  </button>

                  <button
                    css={deleteButton}
                    onClick={() => handleDelete(asset.key)}
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default MediaManager;